import React from 'react';
import { find, size } from 'lodash';
import classNames from 'classnames';
import { FieldModel } from '../../../../models/form/field';
import { FieldType } from '../../../../models/form/fieldType';
import { SectionModel } from '../../../../models/form/section';
import { FormikValues } from 'formik';
import { SelectField } from '../../../../models/form/fields/select';
import { OptionListField } from '../../../../models/form/fields/optionList';
import { PaymentOptionList } from '../../../../models/form/fields/paymentOptionList';
import { PaymentChoiceList } from '../../../../models/form/fields/paymentChoiceList';
import { getText } from '../../../../utilities/CultureUtilities';
import { CultureTextModel } from '../../../../models/form/form';
import { Products } from '../../../../models/form/fields/products';
import _ from 'lodash';

const listFormatter = (
  list: { text: CultureTextModel[] | string; value: string; price?: number }[] | undefined,
  value: string[],
  culture?: string,
) => {
  if (!list || list.length == 0 || value.length == 0) {
    return '-';
  }

  return (
    <React.Fragment>
      {value.map((val, index) => {
        const element = find(list, { value: val });
        if (!element) {
          return null;
        }

        return (
          <div key={`list-string-${index}`} className={classNames('d-flex', { 'mt-1': index > 0 })}>
            <span className="value">{getText(element.text, culture)}</span>
            {element.price && <span className="price">{(element.price / 100).toFixed(2)} $</span>}
          </div>
        );
      })}
    </React.Fragment>
  );
};

const productFormatter = (
  list: { text: CultureTextModel[] | string; value: string; price?: number }[] | undefined,
  value: Array<{ id: string; quantity: number }>,
  culture?: string,
) => {
  const exists = _.filter(value, (product) => product.quantity > 0).length > 0;
  if (!exists) {
    return '-';
  }

  return (
    <React.Fragment>
      {value.map((val, index) => {
        const element = find(list, { value: val.id });
        if (!element || val.quantity == 0) {
          return null;
        }

        let price = (element.price || 0) * val.quantity;
        if (price < 0) {
          price = 0;
        }
        return (
          <div key={`list-string-${val.id}`} className={classNames('d-flex', { 'mt-1': index > 0 })}>
            <span className="value">
              {getText(element.text, culture)}
              <span className="quantity ms-3 text-bold">x{val.quantity}</span>
            </span>
            {price && <span className="price">{(price / 100).toFixed(2)} $</span>}
          </div>
        );
      })}
    </React.Fragment>
  );
};

const formatters = {
  [FieldType.Text]: (_: never, value: string) => (value && value.length > 0 ? value : '-'),
  [FieldType.LongText]: (_: never, value: string) => (value && value.length > 0 ? value : '-'),
  [FieldType.StaticText]: () => false,
  [FieldType.MaskText]: (_: never, value: string) => (value && value.length > 0 ? value : '-'),
  [FieldType.Numeric]: (_: never, value: number) => (value && value.toString().length > 0 ? value : '-'),
  [FieldType.Date]: (_: never, value: string) => (value && value.length > 0 ? value : '-'),
  [FieldType.Time]: (_: never, value: string) => (value && value.length > 0 ? value : '-'),
  [FieldType.Rating]: (_: never, value: number) => (value && value == 0 ? '' : `${value.toString()} ★`),
  [FieldType.YesNo]: (_: never, value: string) => {
    switch (value) {
      case 'yes':
        return 'Yes';
      case 'no':
        return 'No';
      case 'na':
        return 'N/A';
      default:
        return '-';
    }
  },
  [FieldType.Toggle]: (_: never, value: boolean) => (value ? '✓' : '-'),
  [FieldType.CheckBox]: (_: never, value: boolean) => (value ? '✓' : '-'),
  [FieldType.Video]: () => false,
  [FieldType.EmbeddedPhoto]: () => false,
  [FieldType.TakePicture]: () => false,
  [FieldType.DocumentViewer]: () => false,
  [FieldType.UploadFile]: (_: never, value: File) => (value ? value.name : '-'),
  [FieldType.WebLink]: () => false,
  [FieldType.EmailAddress]: (_: never, value: string) => (value && value.length > 0 ? value : '-'),
  [FieldType.Birthdate]: (_: never, value: string) => (value && value.length > 0 ? value : '-'),
  [FieldType.Payment]: () => false,
  [FieldType.Acknowledgment]: (_: never, value: boolean) => (value ? '✓' : '-'),
  [FieldType.Captcha]: () => false,
  [FieldType.Signature]: () => false,
  [FieldType.HTML]: () => false,
  [FieldType.Select]: (field: SelectField, value: string, culture: string) =>
    listFormatter(field.options, [value], culture),
  [FieldType.PaymentOptionList]: (field: PaymentOptionList, value: string[], culture: string) =>
    listFormatter(field.options, value, culture),
  [FieldType.PaymentChoiceList]: (field: PaymentChoiceList, value: string[], culture: string) =>
    listFormatter(field.choices, value, culture),
  [FieldType.OptionList]: (field: OptionListField, value: string[], culture: string) =>
    listFormatter(field.options, value, culture),
  [FieldType.Products]: (field: Products, value: Array<{ id: string; quantity: number }>, culture: string) =>
    productFormatter(field.products, value, culture),
};

export const formatToStringMultiple = (
  section: SectionModel,
  values: FormikValues,
  culture?: string,
): JSX.Element | string | false => {
  if (!values || size(values) == 0) {
    return '-';
  }

  const rows: { rowId: string; fields: FormikValues }[] = [];

  for (const key in values) {
    const [fieldId, rowId] = key.split('@');

    let row = find(rows, { rowId });
    if (!row) {
      row = { rowId, fields: {} };
      rows.push(row);
    }

    row.fields[fieldId] = values[key];
  }

  return (
    <div>
      {rows.map((row) => {
        return (
          <div className="multiple-container" key={`instance-${row.rowId}`}>
            {section.fields.map((field) => {
              const value = formatToString(field, row.fields[field.id]);

              if (value === false) return null;

              return (
                <div key={`summary-field-${field.id}`}>
                  <div className="field-title">{getText(field.text, culture)}</div>
                  <div>{value}</div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export const formatToString = (field: FieldModel, value: any, culture?: string): string | false => {
  if (!formatters[field.type]) {
    if (!value) {
      return '-';
    }
    return value;
  }
  const formattedValue: string | false = formatters[field.type](field, value, culture);
  switch (typeof formattedValue) {
    case 'boolean':
      return formattedValue;
    case 'string':
      return formattedValue.length > 0 ? formattedValue : '-';
    default:
      return formattedValue;
  }
};
